/**
 * 의사결정 페이지 (AIDOS Decision Desk 구현)
 * 요청 생성 → 파일 첨부 → AI 평가 → 리스크/권고 표시 → 승인 → 실행
 */

import { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Chip, Divider,
  CircularProgress, List, ListItemText, ListItemButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, LinearProgress, IconButton, Stepper, Step, StepLabel,
  Stack, Tooltip,
} from '@mui/material';
import {
  Gavel as DecisionIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  PlayArrow as ExecuteIcon,
  AttachFile as AttachIcon,
  Close as CloseIcon,
  Description as DocIcon,
} from '@mui/icons-material';
import { decisionsAPI, ephemeralAPI } from '../services/api';

const riskColors = { LOW: 'success', MEDIUM: 'warning', HIGH: 'error', CRITICAL: 'error' };
const statusLabels = {
  processing: '처리 중', pending_approval: '승인 대기',
  approved: '승인됨', rejected: '거절됨', executed: '실행됨'
};

export default function DecisionsPage() {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [decisions, setDecisions] = useState([]);
  const [selectedDecision, setSelectedDecision] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // 파일 첨부 상태
  const [attachedFiles, setAttachedFiles] = useState([]); // [{name, size}]
  const [uploading, setUploading] = useState(false);
  const [ephemeralSessionId] = useState(`dec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [uploadedDocs, setUploadedDocs] = useState([]); // 업로드 완료된 문서 목록
  const fileInputRef = useRef(null);

  useEffect(() => { loadDecisions(); }, []);

  // 페이지 언마운트 시 ephemeral 세션 정리
  useEffect(() => {
    return () => {
      if (uploadedDocs.length > 0) {
        ephemeralAPI.deleteSession(ephemeralSessionId).catch(() => {});
      }
    };
  }, []);

  const loadDecisions = async () => {
    setListLoading(true);
    try {
      const res = await decisionsAPI.getAll({ size: 50 });
      setDecisions(res.data.data.items || []);
    } catch (e) {
      console.error('Load decisions error:', e);
    } finally {
      setListLoading(false);
    }
  };

  // 파일 선택
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAttachedFiles(prev => [...prev, ...files]);
    }
    e.target.value = '';
  };

  // 파일 제거
  const handleRemoveFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // 파일 업로드 (ephemeral)
  const uploadFiles = async () => {
    if (attachedFiles.length === 0) return true;

    setUploading(true);
    try {
      const response = await ephemeralAPI.upload(attachedFiles, ephemeralSessionId);
      if (response.data.success) {
        const sessionInfo = await ephemeralAPI.getSession(ephemeralSessionId);
        setUploadedDocs(sessionInfo.data.data.documents || []);
        return true;
      }
      return false;
    } catch (e) {
      setSnackbar({ open: true, message: '파일 업로드 실패: ' + (e.response?.data?.error || e.message), severity: 'error' });
      return false;
    } finally {
      setUploading(false);
    }
  };

  // 의사결정 생성
  const handleCreate = async () => {
    if (!content.trim() || content.trim().length < 5) return;
    setLoading(true);

    try {
      // 1. 파일이 있으면 먼저 업로드
      if (attachedFiles.length > 0) {
        const uploaded = await uploadFiles();
        if (!uploaded) {
          setLoading(false);
          return;
        }
      }

      // 2. 의사결정 생성 (ephemeralSessionId 전달)
      const hasFiles = attachedFiles.length > 0 || uploadedDocs.length > 0;
      const res = await decisionsAPI.create({
        request_text: content.trim(),
        ephemeralSessionId: hasFiles ? ephemeralSessionId : undefined
      });

      setSnackbar({ open: true, message: '의사결정이 생성되었습니다.', severity: 'success' });
      setContent('');
      setAttachedFiles([]);
      setSelectedDecision(res.data.data);
      setDetailOpen(true);
      loadDecisions();
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.error || '생성 실패', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (decisionId, action) => {
    try {
      await decisionsAPI.approve(decisionId, { action });
      setSnackbar({ open: true, message: action === 'approved' ? '승인되었습니다.' : '거절되었습니다.', severity: 'success' });
      const res = await decisionsAPI.getById(decisionId);
      setSelectedDecision(res.data.data);
      loadDecisions();
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.error || '처리 실패', severity: 'error' });
    }
  };

  const handleExecute = async (decisionId, option) => {
    try {
      await decisionsAPI.execute(decisionId, { execution_option: option });
      setSnackbar({ open: true, message: '실행이 기록되었습니다.', severity: 'success' });
      const res = await decisionsAPI.getById(decisionId);
      setSelectedDecision(res.data.data);
      loadDecisions();
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.error || '실행 실패', severity: 'error' });
    }
  };

  const handleDelete = async (decisionId) => {
    if (!window.confirm('이 의사결정을 삭제하시겠습니까?')) return;
    try {
      await decisionsAPI.delete(decisionId);
      setSnackbar({ open: true, message: '삭제되었습니다.', severity: 'success' });
      setDetailOpen(false);
      loadDecisions();
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.error || '삭제 실패', severity: 'error' });
    }
  };

  const openDetail = async (decision) => {
    try {
      const res = await decisionsAPI.getById(decision.decision_id);
      setSelectedDecision(res.data.data);
      setDetailOpen(true);
    } catch (e) {
      setSnackbar({ open: true, message: '상세 조회 실패', severity: 'error' });
    }
  };

  const getStepIndex = (d) => {
    if (!d) return 0;
    if (d.status === 'executed') return 3;
    if (d.status === 'approved') return 2;
    if (d.status === 'pending_approval') return 2;
    if (d.evidence?.length > 0) return 1;
    return 0;
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>AIDOS</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        AI Decision Operating System — 내부 문서 근거를 바탕으로 리스크 권고를 확인합니다.
      </Typography>

      {/* 새 의사결정 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>새 의사결정</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          요청 내용(5~1000자)을 입력하고, 근거 문서를 첨부한 뒤 생성합니다.
        </Typography>

        <TextField
          fullWidth multiline rows={4}
          placeholder="의사결정이 필요한 상황을 구체적으로 적어 주세요."
          value={content} onChange={(e) => setContent(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* 파일 첨부 영역 */}
        <Box sx={{ mb: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.md,.hwp,.hwpx"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Tooltip title="근거 문서 첨부 (PDF, DOCX, XLSX 등)">
              <Button
                variant="outlined"
                size="small"
                startIcon={<AttachIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || uploading}
              >
                파일 첨부
              </Button>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              문서를 첨부하면 AI가 근거로 활용합니다. (처리 후 즉시 삭제)
            </Typography>
          </Box>

          {/* 첨부된 파일 목록 */}
          {attachedFiles.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
              {attachedFiles.map((file, i) => (
                <Chip
                  key={i}
                  icon={<DocIcon sx={{ fontSize: 16 }} />}
                  label={file.name}
                  size="small"
                  variant="outlined"
                  color="primary"
                  onDelete={() => handleRemoveFile(i)}
                  deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Stack>
          )}
        </Box>

        {(loading || uploading) && <LinearProgress sx={{ mb: 1 }} />}
        <Button variant="contained" startIcon={<DecisionIcon />}
          onClick={handleCreate} disabled={loading || uploading || content.trim().length < 5}>
          {uploading ? '파일 처리 중...' : loading ? 'AI 분석 중...' : '의사결정 생성'}
        </Button>
      </Paper>

      {/* 목록 */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>목록</Typography>
        {listLoading ? <CircularProgress size={24} /> : decisions.length === 0 ? (
          <Typography color="text.secondary">아직 의사결정 요청이 없습니다.</Typography>
        ) : (
          <List disablePadding>
            {decisions.map((d) => (
              <ListItemButton key={d.id} onClick={() => openDetail(d)} sx={{ borderRadius: 1, mb: 0.5 }}>
                <ListItemText
                  primary={
                    <Typography variant="body2" noWrap sx={{ maxWidth: 500 }}>
                      <strong>{d.decision_id}</strong> — {d.request_text}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      <Chip label={statusLabels[d.status] || d.status} size="small" />
                      {d.risk_level && <Chip label={d.risk_level} size="small" color={riskColors[d.risk_level]} />}
                      {d.risk_score != null && <Chip label={`점수 ${d.risk_score}`} size="small" variant="outlined" />}
                      <Typography variant="caption" color="text.secondary">
                        {new Date(d.created_at).toLocaleString('ko-KR')}
                      </Typography>
                    </Box>
                  }
                />
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(d.decision_id); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemButton>
            ))}
          </List>
        )}
      </Paper>

      {/* 상세 다이얼로그 */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        {selectedDecision && (
          <>
            <DialogTitle>의사결정 상세 — {selectedDecision.decision_id}</DialogTitle>
            <DialogContent dividers>
              <Stepper activeStep={getStepIndex(selectedDecision)} sx={{ mb: 3 }}>
                {['질문', '근거', '판단', '실행'].map((label) => (
                  <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
              </Stepper>

              <Typography variant="subtitle2" color="text.secondary">요청 내용</Typography>
              <Typography sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{selectedDecision.request_text}</Typography>

              <Divider sx={{ my: 2 }} />

              {/* 리스크 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">리스크 점수</Typography>
                  <Typography variant="h3">{selectedDecision.risk_score ?? '—'}</Typography>
                </Box>
                {selectedDecision.risk_level && (
                  <Chip label={selectedDecision.risk_level} color={riskColors[selectedDecision.risk_level]} />
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">권고</Typography>
                  <Typography fontWeight="bold">{selectedDecision.recommendation || '—'}</Typography>
                </Box>
              </Box>

              {/* 근거/이유 */}
              {selectedDecision.reasons?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">근거·이유</Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {selectedDecision.reasons.map((r, i) => <li key={i}><Typography variant="body2">{r}</Typography></li>)}
                  </ul>
                </Box>
              )}

              {/* 근거 문서 */}
              {selectedDecision.evidence?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">수집된 근거 ({selectedDecision.evidence.length}건)</Typography>
                  {selectedDecision.evidence.map((ev, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                        <Chip label={ev.source_name} size="small" variant="outlined" />
                        <Chip label={`신뢰도 ${Math.round(ev.confidence * 100)}%`} size="small"
                          color={ev.confidence < 0.5 ? 'warning' : 'default'} />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {ev.citation_text}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}

              {!selectedDecision.evidence?.length && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  첨부된 근거 문서가 없습니다. 문서를 첨부하면 AI가 근거를 수집하여 더 정확한 분석을 제공합니다.
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/* 승인 처리 */}
              {selectedDecision.status === 'pending_approval' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>승인 처리</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" color="success" startIcon={<ApproveIcon />}
                      onClick={() => handleApprove(selectedDecision.decision_id, 'approved')}>승인</Button>
                    <Button variant="outlined" color="error" startIcon={<RejectIcon />}
                      onClick={() => handleApprove(selectedDecision.decision_id, 'rejected')}>거절</Button>
                  </Box>
                </Box>
              )}

              {/* 실행 */}
              {selectedDecision.status === 'approved' && (selectedDecision.risk_score ?? 100) < 80 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>실행</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(selectedDecision.execution_options || []).map((opt, i) => (
                      <Button key={i} variant="outlined" startIcon={<ExecuteIcon />}
                        onClick={() => handleExecute(selectedDecision.decision_id, opt)}>{opt}</Button>
                    ))}
                  </Box>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Chip label={`상태: ${statusLabels[selectedDecision.status]}`} color="primary" />
                {selectedDecision.final_decision && (
                  <Typography variant="body2" sx={{ mt: 1 }}>최종 실행: {selectedDecision.final_decision}</Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailOpen(false)}>닫기</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
